import React, { useState } from 'react';
import {
  AlertTriangle,
  Clock,
  Users,
  MessageSquare,
  Mail,
  CheckCircle,
  XCircle,
  RefreshCw,
  Zap,
  Target,
  Eye
} from 'lucide-react';
import { tone, toneForSeverity } from "components/ui/tone";
import { clickable } from "components/ui/interactive";
import { SelectField } from "components/ui/select-field";

interface ConflictItem {
  id: string;
  type: 'social_post' | 'email_campaign' | 'ad_campaign';
  title: string;
  platform?: string;
  scheduledTime: Date;
  duration?: number; // minutes
  audience?: {
    size: number;
    overlap?: number; // percentage overlap with conflicting item
  };
  content?: {
    preview: string;
    hashtags?: string[];
    mentions?: string[];
  };
  performance?: {
    expectedReach: number;
    expectedEngagement: number;
  };
}

interface Conflict {
  id: string;
  type: 'timing' | 'audience_overlap' | 'content_similarity' | 'platform_limit';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  items: ConflictItem[];
  suggestions: ConflictSuggestion[];
  impact: {
    reachReduction?: number;
    engagementReduction?: number;
    audienceFatigue?: number;
  };
  detectedAt: Date;
  status: 'unresolved' | 'resolving' | 'resolved' | 'ignored';
  resolvedAt?: Date;
  resolvedBy?: string;
  resolution?: ConflictResolution;
}

interface ConflictSuggestion {
  id: string;
  type: 'reschedule' | 'modify_content' | 'change_audience' | 'merge_campaigns';
  title: string;
  description: string;
  effort: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  estimatedImprovement: {
    reach?: number;
    engagement?: number;
    efficiency?: number;
  };
  actions: Array<{
    itemId: string;
    action: 'reschedule' | 'modify' | 'cancel' | 'merge';
    details: any;
  }>;
}

interface ConflictResolution {
  suggestionId: string;
  appliedActions: Array<{
    itemId: string;
    action: string;
    originalValue: any;
    newValue: any;
    appliedAt: Date;
  }>;
  notes?: string;
}

interface ConflictResolutionUIProps {
  conflicts: Conflict[];
  onResolveConflict: (conflictId: string, resolution: ConflictResolution) => Promise<void>;
  onIgnoreConflict: (conflictId: string, reason: string) => Promise<void>;
  onRefreshConflicts: () => Promise<void>;
}

const ConflictResolutionUI: React.FC<ConflictResolutionUIProps> = ({
  conflicts,
  onResolveConflict,
  onIgnoreConflict,
  onRefreshConflicts
}) => {
  const [selectedConflict, setSelectedConflict] = useState<Conflict | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<ConflictSuggestion | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  const getSeverityColor = (severity) => tone(toneForSeverity(severity)).badge;

  const getSeverityIcon = (severity: Conflict['severity']) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <AlertTriangle className="w-4 h-4" />;
      case 'medium':
        return <Clock className="w-4 h-4" />;
      case 'low':
        return <Eye className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type: Conflict['type']) => {
    switch (type) {
      case 'timing':
        return <Clock className="w-4 h-4" />;
      case 'audience_overlap':
        return <Users className="w-4 h-4" />;
      case 'content_similarity':
        return <MessageSquare className="w-4 h-4" />;
      case 'platform_limit':
        return <Target className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getItemIcon = (type: ConflictItem['type']) => {
    switch (type) {
      case 'email_campaign':
        return <Mail className="w-4 h-4" />;
      case 'social_post':
        return <MessageSquare className="w-4 h-4" />;
      case 'ad_campaign':
        return <Target className="w-4 h-4" />;
      default:
        return <Zap className="w-4 h-4" />;
    }
  };

  const getEffortColor = (effort: ConflictSuggestion['effort']) => {
    switch (effort) {
      case 'low':
        return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
      case 'high':
        return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
      default:
        return 'text-content-secondary bg-surface-sunken';
    }
  };

  const getImpactColor = (impact: ConflictSuggestion['impact']) => {
    switch (impact) {
      case 'high':
        return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
      case 'low':
        return 'text-content-secondary bg-surface-sunken';
      default:
        return 'text-content-secondary bg-surface-sunken';
    }
  };

  const filteredConflicts = conflicts.filter(conflict => {
    if (filterSeverity !== 'all' && conflict.severity !== filterSeverity) {
      return false;
    }
    if (filterType !== 'all' && conflict.type !== filterType) {
      return false;
    }
    return conflict.status === 'unresolved';
  });

  const handleApplySuggestion = async (conflict: Conflict, suggestion: ConflictSuggestion) => {
    try {
      setLoading(true);

      const resolution: ConflictResolution = {
        suggestionId: suggestion.id,
        appliedActions: suggestion.actions.map(action => ({
          itemId: action.itemId,
          action: action.action,
          originalValue: null, // Would be populated with actual original values
          newValue: action.details,
          appliedAt: new Date()
        })),
        notes: resolutionNotes
      };

      await onResolveConflict(conflict.id, resolution);
      
      setSelectedConflict(null);
      setSelectedSuggestion(null);
      setResolutionNotes('');
    } catch (error) {
      console.error('Failed to apply suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnoreConflict = async (conflict: Conflict) => {
    try {
      setLoading(true);
      await onIgnoreConflict(conflict.id, resolutionNotes || 'Manually ignored');
      setSelectedConflict(null);
      setResolutionNotes('');
    } catch (error) {
      console.error('Failed to ignore conflict:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-content">Conflict Resolution</h2>
          <p className="text-content-secondary mt-1">
            Resolve scheduling and coordination conflicts across your campaigns
          </p>
        </div>
        <button
          onClick={onRefreshConflicts}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface p-4 rounded-lg shadow-sm border">
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Severity</label>
            <SelectField
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="border rounded px-3 py-1 text-sm"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </SelectField>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
            <SelectField
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border rounded px-3 py-1 text-sm"
            >
              <option value="all">All Types</option>
              <option value="timing">Timing</option>
              <option value="audience_overlap">Audience Overlap</option>
              <option value="content_similarity">Content Similarity</option>
              <option value="platform_limit">Platform Limit</option>
            </SelectField>
          </div>
          <div className="ml-auto">
            <p className="text-sm text-content-secondary">
              {filteredConflicts.length} unresolved conflict{filteredConflicts.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Conflicts List */}
      {filteredConflicts.length === 0 ? (
        <div className="bg-surface rounded-lg shadow-sm border p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-content mb-2">No Conflicts Found</h3>
          <p className="text-content-secondary">
            All your campaigns are well coordinated with no detected conflicts.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conflicts List */}
          <div className="space-y-4">
            {filteredConflicts.map((conflict) => (
              <div
                key={conflict.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedConflict?.id === conflict.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-line  hover:border-line-strong'
                }`}
                {...clickable(() => setSelectedConflict(conflict))}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(conflict.type)}
                    <h3 className="font-medium text-content">{conflict.title}</h3>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getSeverityColor(conflict.severity)}`}>
                    {getSeverityIcon(conflict.severity)}
                    {conflict.severity}
                  </span>
                </div>

                <p className="text-sm text-content-secondary mb-3">{conflict.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-content-secondary">
                    <span>{conflict.items.length} items affected</span>
                    <span>{conflict.suggestions.length} suggestions</span>
                  </div>
                  <span className="text-xs text-content-muted">
                    {conflict.detectedAt.toLocaleString()}
                  </span>
                </div>

                {/* Impact indicators */}
                {conflict.impact && (
                  <div className="mt-3 flex items-center gap-4 text-xs">
                    {conflict.impact.reachReduction && (
                      <span className="text-red-600 dark:text-red-300">
                        -{conflict.impact.reachReduction}% reach
                      </span>
                    )}
                    {conflict.impact.engagementReduction && (
                      <span className="text-orange-600 dark:text-orange-300">
                        -{conflict.impact.engagementReduction}% engagement
                      </span>
                    )}
                    {conflict.impact.audienceFatigue && (
                      <span className="text-yellow-600 dark:text-yellow-300">
                        +{conflict.impact.audienceFatigue}% fatigue
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Conflict Details */}
          <div className="bg-surface rounded-lg shadow-sm border">
            {selectedConflict ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-content">Conflict Details</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${getSeverityColor(selectedConflict.severity)}`}>
                    {getSeverityIcon(selectedConflict.severity)}
                    {selectedConflict.severity} priority
                  </span>
                </div>

                {/* Affected Items */}
                <div className="mb-6">
                  <h4 className="font-medium text-content mb-3">Affected Items</h4>
                  <div className="space-y-3">
                    {selectedConflict.items.map((item) => (
                      <div key={item.id} className="p-3 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          {getItemIcon(item.type)}
                          <span className="font-medium text-content">{item.title}</span>
                          {item.platform && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-content-secondary text-xs rounded">
                              {item.platform}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-content-secondary">
                          Scheduled: {item.scheduledTime.toLocaleString()}
                        </p>
                        {item.audience && (
                          <p className="text-sm text-content-secondary">
                            Audience: {item.audience.size.toLocaleString()} users
                            {item.audience.overlap && ` (${item.audience.overlap}% overlap)`}
                          </p>
                        )}
                        {item.content && (
                          <p className="text-sm text-content-secondary mt-1">
                            {item.content.preview}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Suggestions */}
                <div className="mb-6">
                  <h4 className="font-medium text-content mb-3">Resolution Suggestions</h4>
                  <div className="space-y-3">
                    {selectedConflict.suggestions.map((suggestion) => (
                      <div
                        key={suggestion.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedSuggestion?.id === suggestion.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                            : 'border-line hover:border-line-strong'
                        }`}
                        {...clickable(() => setSelectedSuggestion(suggestion))}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-medium text-content">{suggestion.title}</h5>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getEffortColor(suggestion.effort)}`}>
                              {suggestion.effort} effort
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(suggestion.impact)}`}>
                              {suggestion.impact} impact
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-content-secondary mb-2">{suggestion.description}</p>
                        
                        {suggestion.estimatedImprovement && (
                          <div className="flex items-center gap-4 text-xs text-green-600 dark:text-green-300">
                            {suggestion.estimatedImprovement.reach && (
                              <span>+{suggestion.estimatedImprovement.reach}% reach</span>
                            )}
                            {suggestion.estimatedImprovement.engagement && (
                              <span>+{suggestion.estimatedImprovement.engagement}% engagement</span>
                            )}
                            {suggestion.estimatedImprovement.efficiency && (
                              <span>+{suggestion.estimatedImprovement.efficiency}% efficiency</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resolution Actions */}
                <div className="border-t pt-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Resolution Notes (Optional)
                    </label>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      rows={3}
                      placeholder="Add notes about this resolution..."
                    />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {selectedSuggestion && (
                      <button
                        onClick={() => handleApplySuggestion(selectedConflict, selectedSuggestion)}
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Apply Solution
                      </button>
                    )}
                    <button
                      onClick={() => handleIgnoreConflict(selectedConflict)}
                      disabled={loading}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      Ignore Conflict
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <AlertTriangle className="w-12 h-12 text-content-subtle mx-auto mb-4" />
                <h3 className="text-lg font-medium text-content mb-2">Select a Conflict</h3>
                <p className="text-content-secondary">
                  Choose a conflict from the list to view details and resolution options.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConflictResolutionUI;